import { useState, useMemo } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react'
import * as HeroIcons from '@heroicons/react/24/outline'
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  UserIcon,
  UsersIcon,
  BriefcaseIcon,
  CalendarIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  ChevronUpDownIcon,
  ShareIcon,
  ClockIcon,
  UserPlusIcon,
  SparklesIcon,
  InboxIcon,
  Cog8ToothIcon,
  TicketIcon,
  KeyIcon,
  EnvelopeIcon,
  CubeIcon,
  CubeTransparentIcon,
  WrenchScrewdriverIcon,
  ArchiveBoxIcon,
  DocumentTextIcon as DocumentTextOutlineIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  AdjustmentsHorizontalIcon,
  DocumentArrowUpIcon,
  QuestionMarkCircleIcon,
  CloudArrowUpIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useCartridgeMenuItems } from '../../hooks/useCartridgeUI'
import type { TranslationKey } from '../../../locales/types'

// Helper to get icon component by name
function getIconByName(iconName: string): typeof UserGroupIcon {
  // Convert icon name to PascalCase with Icon suffix
  // e.g., 'cube-transparent' -> 'CubeTransparentIcon'
  const pascalCase = iconName
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('') + 'Icon'

  // Look up in HeroIcons
  const icon = (HeroIcons as Record<string, unknown>)[pascalCase]
  if (icon && typeof icon === 'function') {
    return icon as typeof UserGroupIcon
  }

  // Fallback to PuzzlePieceIcon for cartridge items
  return PuzzlePieceIcon
}

type NavigationItem = {
  nameKey: TranslationKey
  href: string
  icon: typeof UserGroupIcon
  adminOnly?: boolean
  isCartridge?: boolean
  position?: number
}

const enabledModules = import.meta.env.VITE_ENABLED_MODULES
  ? (import.meta.env.VITE_ENABLED_MODULES as string).split(',').map(s => s.trim())
  : null

function isModuleEnabled(href: string): boolean {
  if (!enabledModules) return true
  return enabledModules.some(m => href === m || (m !== '/' && href.startsWith(m)))
}

const staticNavigation: NavigationItem[] = [
  { nameKey: 'nav.dashboard', href: '/', icon: HomeIcon, position: 0 },
  { nameKey: 'nav.webLeads', href: '/web-leads', icon: InboxIcon, position: 10 },
  { nameKey: 'nav.leads', href: '/leads', icon: UserGroupIcon, position: 20 },
  { nameKey: 'nav.mailingExport', href: '/leads/mailing-export', icon: EnvelopeIcon, position: 25 },
  { nameKey: 'nav.accounts', href: '/accounts', icon: BuildingOfficeIcon, position: 30 },
  { nameKey: 'nav.contacts', href: '/contacts', icon: UserIcon, position: 40 },
  { nameKey: 'nav.opportunities', href: '/opportunities', icon: BriefcaseIcon, position: 50 },
  { nameKey: 'nav.events', href: '/events', icon: CalendarIcon, position: 60 },
  { nameKey: 'nav.tickets', href: '/tickets', icon: TicketIcon, position: 70 },
  { nameKey: 'nav.assets', href: '/assets', icon: CubeIcon, position: 80 },
  { nameKey: 'nav.serviceCases', href: '/service-cases', icon: WrenchScrewdriverIcon, position: 90 },
  { nameKey: 'nav.digitalTwins', href: '/twin', icon: CubeTransparentIcon, position: 100 },
  { nameKey: 'nav.products', href: '/products', icon: ArchiveBoxIcon, position: 110 },
  { nameKey: 'nav.quotes', href: '/quotes', icon: DocumentTextOutlineIcon, position: 120 },
  { nameKey: 'nav.orders', href: '/orders', icon: ShoppingCartIcon, position: 130 },
  { nameKey: 'nav.pdfImport', href: '/cpq/pdf-import', icon: DocumentArrowUpIcon, position: 140 },
  { nameKey: 'nav.proxyProducts', href: '/cpq/proxy-products', icon: QuestionMarkCircleIcon, position: 150 },
  { nameKey: 'nav.approvals', href: '/approvals', icon: CheckCircleIcon, position: 160 },
  { nameKey: 'nav.concierge', href: '/concierge', icon: SparklesIcon, position: 170 },
  { nameKey: 'nav.notes', href: '/notes', icon: DocumentTextIcon, position: 180 },
  { nameKey: 'nav.emails', href: '/emails', icon: EnvelopeIcon, position: 190 },
  { nameKey: 'nav.users', href: '/users', icon: UsersIcon, adminOnly: true, position: 200 },
  { nameKey: 'nav.sharingRules', href: '/settings/sharing-rules', icon: ShareIcon, adminOnly: true, position: 210 },
  { nameKey: 'nav.recordShares', href: '/settings/record-shares', icon: UserPlusIcon, adminOnly: true, position: 220 },
  { nameKey: 'nav.auditLog', href: '/settings/audit-log', icon: ClockIcon, adminOnly: true, position: 230 },
  { nameKey: 'nav.websiteConfigs', href: '/settings/website-configs', icon: Cog8ToothIcon, adminOnly: true, position: 240 },
  { nameKey: 'nav.apiKeys', href: '/settings/api-keys', icon: KeyIcon, adminOnly: true, position: 250 },
  { nameKey: 'nav.cpqRules', href: '/settings/cpq-rules', icon: AdjustmentsHorizontalIcon, adminOnly: true, position: 260 },
  { nameKey: 'nav.sapIntegration', href: '/settings/sap-integration', icon: CloudArrowUpIcon, adminOnly: true, position: 270 },
  { nameKey: 'nav.cartridges', href: '/settings/cartridges', icon: PuzzlePieceIcon, adminOnly: true, position: 280 },
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { items: cartridgeMenuItems } = useCartridgeMenuItems()

  // Merge static navigation with cartridge menu items
  const navigation = useMemo(() => {
    const cartridgeItems: NavigationItem[] = cartridgeMenuItems.map(({ item }) => ({
      nameKey: item.labelKey as TranslationKey,
      href: item.href,
      icon: getIconByName(item.icon),
      adminOnly: item.adminOnly,
      isCartridge: true,
      position: typeof item.position === 'number' ? item.position : 95, // Default position after static items
    }))

    return [...staticNavigation, ...cartridgeItems].sort(
      (a, b) => (a.position ?? 50) - (b.position ?? 50)
    )
  }, [cartridgeMenuItems])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const userInitials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || user.email[0].toUpperCase()
    : 'U'
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    : 'User'

  return (
    <>
      <div className="min-h-screen bg-gray-100">
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                  </button>
                </div>
              </TransitionChild>

              {/* Mobile Sidebar */}
              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
                <div className="relative flex h-16 shrink-0 items-center">
                  <h1 className="text-xl font-bold text-brand-500 flex items-center">
                    JustSell<img src="/logo.png" alt="E" className="h-6 w-6 mx-1" />ngine
                  </h1>
                </div>
                <nav className="relative flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation
                          .filter(item => !item.adminOnly || user?.role === 'admin')
                          .map((item) => {
                          const isActive = item.href === '/'
                            ? location.pathname === '/'
                            : location.pathname.startsWith(item.href)
                          const enabled = isModuleEnabled(item.href)
                          return (
                            <li key={item.nameKey}>
                              {enabled ? (
                              <Link
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={classNames(
                                  isActive
                                    ? 'bg-brand-500 text-white'
                                    : 'text-gray-700 hover:bg-brand-50 hover:text-brand-700',
                                  'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold transition-colors'
                                )}
                              >
                                <item.icon
                                  aria-hidden="true"
                                  className={classNames(
                                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-brand-600',
                                    'size-6 shrink-0 transition-colors'
                                  )}
                                />
                                {t(item.nameKey)}
                              </Link>
                              ) : (
                              <span
                                className="group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-300 cursor-not-allowed select-none"
                                title="Coming soon"
                              >
                                <item.icon
                                  aria-hidden="true"
                                  className="size-6 shrink-0 text-gray-200"
                                />
                                {t(item.nameKey)}
                              </span>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </li>
                    <li className="mt-auto">
                      <div className="flex items-center gap-x-3 px-2 py-3">
                        <GlobeAltIcon className="size-5 text-gray-400" />
                        <LanguageSwitcher />
                      </div>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-72 lg:flex-col lg:p-4">
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
            <div className="relative flex h-16 shrink-0 items-center">
              <h1 className="text-2xl font-bold text-brand-500 flex items-center">
                JustSell<img src="/logo.png" alt="E" className="h-7 w-7 mx-1" />ngine
              </h1>
            </div>
            <nav className="relative flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation
                      .filter(item => !item.adminOnly || user?.role === 'admin')
                      .map((item) => {
                      const isActive = item.href === '/'
                        ? location.pathname === '/'
                        : location.pathname.startsWith(item.href)
                      const enabled = isModuleEnabled(item.href)
                      return (
                        <li key={item.nameKey}>
                          {enabled ? (
                          <Link
                            to={item.href}
                            className={classNames(
                              isActive
                                ? 'bg-brand-500 text-white'
                                : 'text-gray-700 hover:bg-brand-50 hover:text-brand-700',
                              'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold transition-colors'
                            )}
                          >
                            <item.icon
                              aria-hidden="true"
                              className={classNames(
                                isActive ? 'text-white' : 'text-gray-400 group-hover:text-brand-600',
                                'size-6 shrink-0 transition-colors'
                              )}
                            />
                            {t(item.nameKey)}
                          </Link>
                          ) : (
                          <span
                            className="group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-300 cursor-not-allowed select-none"
                            title="Coming soon"
                          >
                            <item.icon
                              aria-hidden="true"
                              className="size-6 shrink-0 text-gray-200"
                            />
                            {t(item.nameKey)}
                          </span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </li>
                <li className="mt-auto">
                  <div className="flex items-center gap-x-3 px-2 py-3">
                    <GlobeAltIcon className="size-5 text-gray-400" />
                    <LanguageSwitcher />
                  </div>
                </li>
                <li className="-mx-6">
                  <Menu as="div" className="relative">
                    <MenuButton className="flex w-full items-center gap-x-4 px-6 py-3 text-sm/6 font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                      <div className="size-8 rounded-full flex items-center justify-center text-white font-semibold bg-brand-500">
                        {userInitials}
                      </div>
                      <span className="flex-1 text-left">{userName}</span>
                      <ChevronUpDownIcon className="size-5 text-gray-400" />
                    </MenuButton>
                    <MenuItems className="absolute bottom-full left-0 right-0 mb-1 mx-4 rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <MenuItem>
                        <Link
                          to="/settings/2fa"
                          className="flex items-center gap-x-3 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-50"
                        >
                          <ShieldCheckIcon className="size-5 text-gray-400" />
                          Two-Factor Auth
                        </Link>
                      </MenuItem>
                      <MenuItem>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-50"
                        >
                          <ArrowRightOnRectangleIcon className="size-5 text-gray-400" />
                          Sign out
                        </button>
                      </MenuItem>
                    </MenuItems>
                  </Menu>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        {/* Mobile header */}
        <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden mx-4 mt-4 rounded-xl">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="relative -m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
          <div className="relative flex-1 text-sm/6 font-semibold text-gray-900">
            {(() => {
              const item = navigation.find(item => location.pathname.startsWith(item.href))
              return item ? t(item.nameKey) : 'JustSellEngine'
            })()}
          </div>
          <Menu as="div" className="relative">
            <MenuButton className="relative">
              <span className="sr-only">{t('nav.profile')}</span>
              <div className="size-8 rounded-full flex items-center justify-center text-white font-semibold bg-brand-500">
                {userInitials}
              </div>
            </MenuButton>
            <MenuItems className="absolute right-0 top-full mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
              <MenuItem>
                <Link
                  to="/settings/2fa"
                  className="flex items-center gap-x-3 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-50"
                >
                  <ShieldCheckIcon className="size-5 text-gray-400" />
                  Two-Factor Auth
                </Link>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-50"
                >
                  <ArrowRightOnRectangleIcon className="size-5 text-gray-400" />
                  Sign out
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>

        <main className="lg:pl-72">
          <Outlet />
        </main>
      </div>
    </>
  )
}
